import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Logger } from '@saviynt/common';
import {
  ApplicationIcon,
  Badge,
  Box,
  Icon,
  type IconKind,
  Illustration,
  type IllustrationKind,
  ListRow,
  ListRowGroup,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@saviynt/design-system';

import { SodListLoader } from '../../../../../shared/components';
import {
  CF_NEU_BLD,
  CF_SEC_MED,
  SORT_ACTION_MAPPING,
  VIOLATIONS_TAB_LIST_AD_FILTER,
  VIOLTIONS_LIST_SORT_FIELDS,
} from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useInViewObserver,
  useTypedSelector,
} from '../../../../../shared/hooks';
import {
  type ListRowAccountState,
  type ListRowState,
  type TrackedFunctionData,
} from '../../../../../shared/types';
import { type ViolationAssignedEntitlements } from '../../../../Details/data-layer/detailsTypes';
import { useConflictApiEndpoints } from '../../../../Details/hooks';
import { useGetViolationsListMutation } from '../../../data-layer/violationsTabApi';
import { type FunctionAnalysisState } from '../../../data-layer/violationsTabTypes';
import { getListPayload } from '../../../utils';

import {
  RemediateFunctionAsgEntitlement,
  RemediateImpactAccountCenterContent,
  RemediateImpactViolationCenterContent,
  RemediateScreenError,
  RemediateScreenLoader,
} from './RemediateScreensShared';
import { getTitleText } from './utils';
import { type ActiveScreenType } from './ViolationsRemediateAction';

const MAX_COUNT = 20;

interface RemediationImpactScreenProps {
  sodKey: string;
  activeScreen: ActiveScreenType;
  functionAnalysis: FunctionAnalysisState | null;
  accounts: ListRowAccountState[];
  fnKeys: string[] | null;
  userName: string;
  displayName: string;
  functions: TrackedFunctionData[];
}

/**
 * RemediationImpactScreen
 */
function RemediationImpactScreen({
  sodKey,
  activeScreen,
  functionAnalysis,
  accounts,
  fnKeys,
  userName,
  displayName,
  functions,
}: RemediationImpactScreenProps) {
  const l = useInvertedLocalize();
  const sreeenRef = useRef<HTMLDivElement | null>(null);
  const {
    controls: { tabInAction },
  } = useTypedSelector((state) => state.aag.violationsTab);

  // General
  const activeFnKey = useMemo(() => fnKeys?.[0], [fnKeys]);
  const [stats, setStats] = useState({
    entitlementCount: 0,
    accountCount: accounts.length,
    violationCount: 0,
  });
  const [selection, setSelection] = useState(1);

  // Entitlements
  const [currentToken, setCurrentToken] = useState<string | null>(null);
  const [assignedEntitlementListRows, setAssignedEntitlementListRows] =
    useState<ViolationAssignedEntitlements[]>([]);

  // Violations
  const [currentOffset, setCurrentOffset] = useState(0);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [violationListRows, setViolationListRows] = useState<ListRowState[]>(
    []
  );

  // Observer
  const [isObserverInView, setIsObserverInView] = useState(false);
  const listObserverElement = useInViewObserver(({ isInView }) => {
    setIsObserverInView(isInView);
  });

  // Endpoints
  const {
    handleFetchAssignedEntitlements,
    states: { isAssignedEntilementError, isAssignedEntilementLoading },
  } = useConflictApiEndpoints([], null);
  const [
    fetchViolationsList,
    { isLoading: isViolationListLoading, isError: isViolationListError },
  ] = useGetViolationsListMutation();
  // Constants
  const TOGGLE_BTNS = useMemo(
    () => [
      {
        id: 1,
        label: l('Entitlement'),
        icon: 'Entitlement',
        count: stats.entitlementCount,
      },
      {
        id: 2,
        label: l('Account'),
        icon: 'Account',
        count: stats.accountCount,
      },
      {
        id: 3,
        label: l('Violation'),
        icon: 'SOD',
        count: stats.violationCount,
      },
    ],
    [l, stats]
  );
  const isValidEntitlementCall = useMemo(
    () =>
      Boolean(
        (selection === 1 &&
          !isAssignedEntilementError &&
          (currentToken || assignedEntitlementListRows.length === 0)) ||
          (selection === 3 &&
            !isViolationListError &&
            (totalCount === null ||
              (currentOffset < totalCount &&
                violationListRows.length < totalCount)))
      ),
    [
      selection,
      isAssignedEntilementError,
      currentToken,
      assignedEntitlementListRows.length,
      isViolationListError,
      totalCount,
      currentOffset,
      violationListRows.length,
    ]
  );

  // API calls
  const triggerFetchAssignedEntitlements = useCallback(() => {
    if (fnKeys) {
      handleFetchAssignedEntitlements(sodKey, fnKeys[0], currentToken, {
        maxCount: MAX_COUNT,
      })
        .then((result) => {
          const { data: response, currentAfterKey } = result;

          setCurrentToken(currentAfterKey);
          setAssignedEntitlementListRows((prev) => [...prev, ...response]);
        })
        .catch((error) => {
          Logger.log('Error getting entitlements', error);
        })
        .finally(() => {
          setIsObserverInView(false);
        });
    }
  }, [fnKeys, sodKey, currentToken, handleFetchAssignedEntitlements]);

  const handleFetchViolationList = useCallback(
    async (offset: number) => {
      const activeFiltersMap = {
        [VIOLATIONS_TAB_LIST_AD_FILTER.FUNCTION.value]: [
          ...new Set(
            functions
              .filter((fn) => fn.functionKey === activeFnKey)
              .map((fn) => fn.functionName)
          ),
        ],
        [VIOLATIONS_TAB_LIST_AD_FILTER.NAME.value]: [
          ...new Set(accounts.map((acc) => acc.accountKey)),
        ],
      };

      const payload = getListPayload(
        activeFiltersMap,
        tabInAction ?? undefined,
        null,
        {
          value: VIOLTIONS_LIST_SORT_FIELDS.UPDATEDATE.value,
          order: SORT_ACTION_MAPPING.DESC,
        },
        null,
        { max: MAX_COUNT, offset },
        null
      );

      try {
        const controller = fetchViolationsList(payload);

        const response = await controller.unwrap();

        return response;
      } catch {
        throw Error('Error getting violations for remediate action');
      }
    },
    [fetchViolationsList, functions, tabInAction, activeFnKey, accounts]
  );

  const triggerFetchViolationsList = useCallback(() => {
    handleFetchViolationList(currentOffset)
      .then((result) => {
        const { data, totalRecords } = result as {
          data: ListRowState[];
          totalRecords: number;
        };

        if (Array.isArray(data)) {
          setCurrentOffset((prev) => prev + MAX_COUNT);
          setTotalCount(Number(totalRecords));
          setViolationListRows((prev) => [...prev, ...data]);
        } else {
          setTotalCount(0);
        }
      })
      .catch((error) => {
        Logger.log('Error getting violations', error);
      })
      .finally(() => {
        setIsObserverInView(false);
      });
  }, [currentOffset, handleFetchViolationList]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isObserverInView) {
        return;
      }

      if (selection === 1) {
        triggerFetchAssignedEntitlements();

        return;
      }

      if (selection === 3) {
        triggerFetchViolationsList();
      }
    }, 10);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should only depend on observer
  }, [isObserverInView]);

  useEffect(() => {
    setStats((prev) => ({
      ...prev,
      entitlementCount:
        functionAnalysis?.functionEntitlementCount.find(
          (a) => a.functionId === Number(activeFnKey)
        )?.entitlementCount ?? 0,
    }));
  }, [functionAnalysis, activeFnKey]);

  // Reset
  useEffect(() => {
    if (sreeenRef.current && activeScreen === 2) {
      setSelection(1);
      setCurrentToken(null);
      setAssignedEntitlementListRows([]);

      setCurrentOffset(0);
      setViolationListRows([]);

      sreeenRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [activeScreen]);

  useEffect(() => {
    if (totalCount !== null) {
      setStats((prev) => ({
        ...prev,
        violationCount: totalCount,
      }));
    }
  }, [totalCount]);

  const renderBody = useCallback(() => {
    if (!fnKeys) {
      return <RemediateScreenLoader />;
    }

    if (isAssignedEntilementError) {
      return (
        <RemediateScreenError onRefresh={() => setIsObserverInView(true)} />
      );
    }

    if (selection === 1 || selection === 3) {
      return (
        <ListRowGroup>
          {selection === 1 &&
            assignedEntitlementListRows.map(
              ({
                entitlementKey,
                entitlementTypeName,
                entitlementDisplayName,
                entitlementValue,
              }) => (
                <ListRow
                  key={entitlementKey}
                  leadingComp={<Icon kind='Entitlement' color={CF_SEC_MED} />}
                  centerContent={
                    <RemediateFunctionAsgEntitlement
                      entTypeName={entitlementTypeName}
                      displayName={entitlementDisplayName}
                      entValue={entitlementValue}
                    />
                  }
                  isListItem={false}
                  isCheckboxDisabled
                />
              )
            )}
          {selection === 3 &&
            violationListRows.map(
              ({
                sodKey: id,
                risk,
                actualVsPotential,
                endpoints,
                user,
                accounts: violationAccounts,
              }) => (
                <ListRow
                  key={id}
                  centerContent={
                    <RemediateImpactViolationCenterContent
                      riskName={risk.riskName}
                      riskPriority={risk.riskSeverity ?? risk.riskPriority}
                      riskScore={risk.riskScore}
                      actualVsPotential={actualVsPotential}
                      endpoints={endpoints}
                      firstName={user.firstName}
                      lastName={user.lastName}
                      username={user.username}
                      accounts={violationAccounts}
                    />
                  }
                  isListItem={false}
                  isCheckboxDisabled
                />
              )
            )}
          {selection === 3 &&
            violationListRows.length === 0 &&
            !isViolationListLoading && (
              <Box tag='div' className='AccessModal-illustrationWrapper'>
                <Illustration kind={'Empty' as IllustrationKind} />{' '}
                <Typography kind='h1'>{l('No Results Found!')}</Typography>
              </Box>
            )}
          {isValidEntitlementCall && (
            <>
              {listObserverElement}
              <SodListLoader count={3} />
            </>
          )}
        </ListRowGroup>
      );
    }

    if (selection === 2) {
      return (
        <ListRowGroup>
          {accounts.map(({ accountName, endpointLogo }) => (
            <ListRow
              key={accountName}
              leadingComp={<ApplicationIcon logo='CertsAccount' />}
              centerContent={
                <RemediateImpactAccountCenterContent
                  userName={userName}
                  endpoints={[
                    {
                      endpointName: accountName,
                      endpointLogo,
                    },
                  ]}
                  displayName={displayName}
                />
              }
              isListItem={false}
              isCheckboxDisabled
            />
          ))}
          {isValidEntitlementCall && (
            <>
              {listObserverElement}
              <SodListLoader count={3} />
            </>
          )}
        </ListRowGroup>
      );
    }

    return <RemediateScreenLoader />;
  }, [
    l,
    fnKeys,
    selection,
    assignedEntitlementListRows,
    isValidEntitlementCall,
    listObserverElement,
    accounts,
    userName,
    displayName,
    isAssignedEntilementError,
    isViolationListLoading,
    violationListRows,
  ]);

  return (
    <Box tag='div' className='RemediationImpactScreen'>
      <div ref={sreeenRef} className='RemediationImpactScreen-listWrapper'>
        <Box tag='div' className='RemediationImpactScreen-summary'>
          <Typography kind='body2' color={CF_SEC_MED}>
            {l('IMPACT SUMMARY')}
          </Typography>
          <Typography
            kind='h2'
            color={CF_NEU_BLD}
            className='RemediationImpactScreen-summaryStats'>
            {l('Revoke')}
            <span className='RemediationImpactScreen-summaryStats--isNumber'>
              {getTitleText('entitlement', stats)}
            </span>
            {l('from')}
            <span className='RemediationImpactScreen-summaryStats--isNumber'>
              {getTitleText('account', stats)}
            </span>
            {l('to remediate')}
            <span className='RemediationImpactScreen-summaryStats--isNumber'>
              {getTitleText('violation', stats)}
            </span>
          </Typography>
        </Box>
        <ToggleButtonGroup
          selection={selection}
          setSelection={setSelection}
          className='RemediationImpactScreen-toggler'
          isDisabled={isAssignedEntilementLoading || isViolationListLoading}>
          {TOGGLE_BTNS.map((btn) => (
            <ToggleButton
              key={btn.id}
              label={btn.label}
              icon={<Icon kind={btn.icon as IconKind} />} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
              BadgeComp={
                btn.count !== 0 ? (
                  <Badge
                    kind='number'
                    badgeStyle='outlined'
                    number={btn.count}
                    color='info'
                  />
                ) : undefined
              }
            />
          ))}
        </ToggleButtonGroup>
        <Box tag='div' className='RemediationImpactScreen-listContent'>
          {renderBody()}
        </Box>
      </div>
    </Box>
  );
}

export default memo(RemediationImpactScreen);
